// The platform's insurance line-of-business vocabulary: three categories, two plan types
// each. Shared model because two unrelated modules classify against the same six values —
// the platform-clinic agreements (super) and the Stedi eligibility plan-type mapper
// (plugins/stedi) — and neither may import the other's internals.

export const INSURANCE_CATEGORIES = [
  { value: 'medicare', label: 'Medicare' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'medicaid', label: 'Medicaid' },
] as const

export type InsuranceCategory = (typeof INSURANCE_CATEGORIES)[number]['value']

// Plan types are constrained by category; the agreements contract refine and the web
// row editor share this single mapping.
export const PLAN_TYPES_BY_CATEGORY = {
  medicare: [
    { value: 'original-medicare', label: 'Original Medicare' },
    { value: 'medicare-advantage', label: 'Medicare Advantage' },
  ],
  commercial: [
    { value: 'commercial-ppo', label: 'Commercial PPO' },
    { value: 'commercial-hmo', label: 'Commercial HMO' },
  ],
  medicaid: [
    { value: 'state-medicaid', label: 'State Medicaid' },
    { value: 'medicaid-hmo', label: 'Medicaid HMO' },
  ],
} as const satisfies Record<InsuranceCategory, readonly { value: string; label: string }[]>

export const INSURANCE_PLAN_TYPES = [
  'original-medicare',
  'medicare-advantage',
  'commercial-ppo',
  'commercial-hmo',
  'state-medicaid',
  'medicaid-hmo',
] as const

export type InsurancePlanType = (typeof INSURANCE_PLAN_TYPES)[number]

export function planTypesFor(category: string | undefined): InsurancePlanType[] {
  if (!category || !(category in PLAN_TYPES_BY_CATEGORY)) return []
  return PLAN_TYPES_BY_CATEGORY[category as InsuranceCategory].map((p) => p.value)
}

// The systems that carry this vocabulary on FHIR resources: Contract.identifier on
// platform-clinic agreements, and meta.tag on CoverageEligibilityResponse (the
// payer-verified classification an eligibility check produced).
export const INSURANCE_CATEGORY_SYSTEM = 'https://behuman.health/fhir/insurance-category'
export const INSURANCE_PLAN_TYPE_SYSTEM = 'https://behuman.health/fhir/insurance-plan-type'
